"use client";

import {
	faCopy,
	faFileCsv,
	faFloppyDisk,
	faPieChart,
	faPowerOff,
	faShare,
} from "@fortawesome/pro-solid-svg-icons";
import { useMemo, useState } from "react";
import { match } from "ts-pattern";
import { toggleUseRestPieces } from "~/app/[lang]/cart/actions/restPieces";
import { useCommand } from "~/app/[lang]/command/Context";
import { CommandItem } from "~/app/[lang]/command/utils/Item";
import type { LangParams } from "~/app/[lang]/layout";
import { useBetterAction } from "~/hooks/useBetterAction";
import type { CommandCartType } from "../Cart";

interface Props extends LangParams {
	cart: CommandCartType;
	title: string;
}

export function CommandItemuseBoards({ lang, cart, title }: Props) {
	const { executeAsync } = useBetterAction(toggleUseRestPieces);

	const completePanels = useMemo(() => {
		return cart?.items.filter((item) => item.group === "custom_cart_group_hele_platen");
	}, [cart?.items]);

	const loading = useMemo(() => {
		return completePanels?.some((item) => !item.isOptimized);
	}, [completePanels]);

	const [useBoards, setUseBoards] = useState<boolean>(!!cart?.useRestPieces);

	return (
		<CommandItem
			variant="default"
			icon={faPieChart}
			active={useBoards}
			disabled={!completePanels?.length}
			isLoading={loading}
			onSelect={async () => {
				if (cart?.id) {
					setUseBoards(!useBoards);

					await executeAsync({ lang, value: !useBoards });
				}
			}}
		>
			{title}
		</CommandItem>
	);
}

interface CommandItemCartProps {
	cart: CommandCartType;
	variant: "copy" | "share" | "new" | "save" | "export";
	title: string;
}

export function CommandItemCart({ cart, variant, title }: CommandItemCartProps) {
	const {
		copyCartCallback,
		shareCartCallback,
		newCartCallback,
		saveProjectCallback,
		exportCartToCSVCallback,
	} = useCommand();

	return (
		<CommandItem
			disabled={!cart}
			icon={match(variant)
				.with("copy", () => faCopy)
				.with("share", () => faShare)
				.with("new", () => faPowerOff)
				.with("save", () => faFloppyDisk)
				.with("export", () => faFileCsv)
				.exhaustive()}
			shortcut={match(variant)
				.with("copy", () => "c")
				.with("share", () => "d")
				.with("new", () => "n")
				.with("save", () => "s")
				.with("export", () => "f")
				.exhaustive()}
			onSelect={() =>
				match(variant)
					.with("copy", () => copyCartCallback())
					.with("share", () => shareCartCallback())
					.with("new", () => newCartCallback())
					.with("save", () => saveProjectCallback())
					.with("export", () => exportCartToCSVCallback())
					.exhaustive()
			}
		>
			{title}
		</CommandItem>
	);
}
